// libs
import React, { useState, useEffect, useLayoutEffect } from 'react';
import { RouteComponentProps } from 'react-router-dom';

// components
import ErrorScreen from 'shared/components/VisitorPriortization/ErrorScreen';

// helpers
import {
  isPathBlocked,
  setOnBlockCallback,
  subscribeVPDocUpdates,
} from 'shared/components/VisitorPriortization/helpers/csr';
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';

// global components
import ChunkFallback from 'shared/components/ChunkFallback';

// defs
type ComponentType = React.ComponentClass | React.FC;

interface IRouteRenderProps extends RouteComponentProps<any> {
  Component: ComponentType;
  routes?: string[];
}

const useIsomorphicLayoutEffect = __BROWSER__ ? useLayoutEffect : useEffect;

function RouteRender(routeProps: IRouteRenderProps) {
  const [showVPScreen, setShowVPScreen] = useState(false);
  const { enabled: isVPEnabled = false } = useRemoteConfig('visitorPriortization', {
    enabled: false,
  });
  const { Component, ...props } = routeProps;

  // Block before paint if VP doc is already available (e.g. cached in sessionStorage).
  // useLayoutEffect runs after hydration commit but before browser paint,
  // so the user never sees SSR content if they're blocked.
  useIsomorphicLayoutEffect(() => {
    if (!isVPEnabled) {
      return;
    }

    if (isPathBlocked(routeProps.location.pathname)) {
      setShowVPScreen(true);
    }
  }, [isVPEnabled, routeProps.location.pathname]);

  useEffect(() => {
    const onError = () => {
      setShowVPScreen(true);
    };

    const clearOnBlockCallback = setOnBlockCallback(onError);
    const clearVPDocSubscription = subscribeVPDocUpdates((vpDoc) => {
      if (__BROWSER__ && vpDoc !== undefined && isPathBlocked(window.location.pathname)) {
        setShowVPScreen(true);
      }
    });

    return () => {
      clearVPDocSubscription();
      clearOnBlockCallback();
    };
  }, []);

  if (showVPScreen) {
    return <ErrorScreen />;
  }

  // @ts-ignore -- route components may accept a fallback prop in lazy loaded chunks.
  return (
    <>
      <Component {...props} fallback={<ChunkFallback />} />
    </>
  );
}

function customRoute(Component: ComponentType, routes: string[]) {
  // eslint-disable-next-line react/display-name
  return (props: RouteComponentProps<any>) => {
    return <RouteRender {...props} routes={routes} Component={Component} />;
  };
}

export default customRoute;
